import { Component, HostBinding, ViewEncapsulation, ViewChild, signal, ChangeDetectorRef } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { forkJoin, of, switchMap, from, firstValueFrom } from 'rxjs'
import { map } from 'rxjs/operators'
import { IDropdownOption } from 'src/app/shared/models/dropdown-option.interface'
import {
   ApplicationArea,
   ReviewPlan,
   UserReview,
   UserReviewClient,
   DVMToolSelectedOption,
   DVMToolSelectionType,
   ReviewAttachmentDto,
   FileParameter,
} from 'src/app/shared/services/ppv.api'
import { DVM_REVIEW_TEMPLATE } from './review-template-dvm'
import { HM_REVIEW_TEMPLATE } from './review-template-hm'
import { COS_REVIEW_TEMPLATE } from './review-template-cos'
import { DO_REVIEW_TEMPLATE } from './review-template-do'
import { RM_REVIEW_TEMPLATE } from './review-template-rm'
import { RECEPTION_REVIEW_TEMPLATE, resolveTemplateTokens_reception } from './review-template-reception'
import { CROSS_TRAINED_REVIEW_TEMPLATE, resolveTemplateTokens_crosstrained } from './review-template-cross-trained'
import { CLINICAL_REVIEW_TEMPLATE, resolveTemplateTokens_clinical } from './review-template-clinical'
import { VcRoundedCardComponent } from 'src/app/shared/components/rounded-card/rounded-card.component'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { AuthManager } from 'src/app/shared/auth/AuthManager'
import { ToastrService } from 'ngx-toastr'
import { MatDialog } from '@angular/material/dialog'
import { ReviewAssessmentSectionsComponent } from './components/review-assessment-sections/review-assessment-sections.component'
import { ConfirmDialogComponent } from './components/confirm-dialog.component'
import { HttpClient } from '@angular/common/http'
import { PeerInputResponse, parseResponseValue } from './peer-input'

dayjs.extend(utc)

const JOB_DESC_BASE = 'https://vetcor.skyworld.com/userfilesV3/files/document-library/Performance%20Hub/'

const JOB_DESCRIPTIONS: Record<string, { label: string; url: string }> = {
   CCS: { label: 'Client Care Representative', url: JOB_DESC_BASE + 'Client%20Care%20Representative%20Job%20Description%20Final.pdf' },
   'CCS-L': {
      label: 'Lead Client Care Representative',
      url: JOB_DESC_BASE + 'Lead%20Client%20Care%20Representative%20Job%20Description%20Final.pdf',
   },
   'GROOM--G': { label: 'Groomer', url: JOB_DESC_BASE + 'Groomer%20Job%20Description%20Final.pdf' },
   'PCA-L': { label: 'Lead Kennel Attendant', url: JOB_DESC_BASE + 'Lead%20Kennel%20Attendant%20Job%20Description%20Final.pdf' },
   'PCA / Kennel LAH': { label: 'Kennel Attendant', url: JOB_DESC_BASE + 'Kennel%20Attendant%20Job%20Description%20Final.pdf' },
   'PETCARE-': { label: 'Kennel Attendant', url: JOB_DESC_BASE + 'Kennel%20Attendant%20Job%20Description%20Final.pdf' },
   TECH: { label: 'Veterinary Technician', url: JOB_DESC_BASE + 'Veterinary%20Technician%20Job%20Description%20Final.pdf' },
   'TECH-AST': { label: 'Veterinary Assistant', url: JOB_DESC_BASE + 'Veterinary%20Assistant%20Job%20Description%20Final.pdf' },
   'TECH-L': { label: 'Lead Veterinary Technician', url: JOB_DESC_BASE + 'Lead%20Veterinary%20Technician%20Job%20Description%20Final.pdf' },
   'TECH-LVT': {
      label: 'Credentialed Veterinary Technician',
      url: JOB_DESC_BASE + 'Credentialed%20Veterinary%20Technician%20Job%20Description%20Final.pdf',
   },
   LEAD_TECH_LVT: {
      label: 'Lead Credentialed Veterinary Technician',
      url: JOB_DESC_BASE + 'Lead%20Credentialed%20Veterinary%20Technician%20Job%20Description%20Final.pdf',
   },
   INVENTORY_MANAGER: { label: 'Veterinary Technician', url: JOB_DESC_BASE + 'Veterinary%20Technician%20Job%20Description%20Final.pdf' },
   LEAD_VET_ASSISTANT: { label: 'Veterinary Assistant', url: JOB_DESC_BASE + 'Veterinary%20Assistant%20Job%20Description%20Final.pdf' },
   VET_ASSISTANT_LG_ANIMAL: { label: 'Veterinary Assistant', url: JOB_DESC_BASE + 'Veterinary%20Assistant%20Job%20Description%20Final.pdf' },
   MAINT: { label: 'Kennel Attendant', url: JOB_DESC_BASE + 'Kennel%20Attendant%20Job%20Description%20Final.pdf' },
   AHM: { label: 'Assistant Hospital Manager', url: JOB_DESC_BASE + 'Assistant%20Hospital%20Manager%20Job%20Description%20Final.pdf' },
   BFM: { label: 'Lead Kennel Attendant', url: JOB_DESC_BASE + 'Lead%20Kennel%20Attendant%20Job%20Description%20Final.pdf' },
}

@Component({
   selector: 'dvm-tool-plan',
   templateUrl: './dvm-tool-plan.component.html',
   styleUrls: ['./dvm-tool-plan.component.scss'],
   standalone: false,
   encapsulation: ViewEncapsulation.None,
})
export class DVMToolPlanComponent {
   public reviewTemplate_DVM = DVM_REVIEW_TEMPLATE
   public reviewTemplate_HM = HM_REVIEW_TEMPLATE
   public reviewTemplate_COS = COS_REVIEW_TEMPLATE
   public reviewTemplate_DO = DO_REVIEW_TEMPLATE
   public reviewTemplate_RM = RM_REVIEW_TEMPLATE
   public reviewTemplate_RECEPTION = RECEPTION_REVIEW_TEMPLATE
   public reviewTemplate_CROSSTRAINED = CROSS_TRAINED_REVIEW_TEMPLATE
   public reviewTemplate_CLINICAL = CLINICAL_REVIEW_TEMPLATE

   @ViewChild('revieweeSections') revieweeSections?: ReviewAssessmentSectionsComponent
   @ViewChild('reviewerSections') reviewerSections?: ReviewAssessmentSectionsComponent

   @HostBinding('class.full-flex-height') fullFlexHeight = true
   showBackToTop: boolean = false
   allSectionsLoaded: boolean = false
   isUnderReview: boolean = false

   isForDVM: boolean = false
   reviewType: string = 'DVM'
   reviewTypeSet: boolean = true

   submittedByAdmin: boolean = false
   submittedByAdminClicked: boolean = false
   submittedByAdminReviewer: boolean = false
   submittedByAdminReviewerClicked: boolean = false
   adminOnlyAttachmentExpanded: boolean = false
   isUploadingAttachment: boolean = false
   reviewAttachments: ReviewAttachmentDto[] = []

   printReviewee: boolean = false
   printReviewer: boolean = false
   printerModalOpened: boolean = false
   printChoiceDialog = signal<{ show: boolean }>({ show: false })
   printInSpanish = false
   isTranslating = false

   revieweeId: string = ''
   loggedInUser: string = ''
   loggedInUserDF: string = ''
   loggedInUserId: number
   performanceHubAdmin: number[] = [79833, 4242] // set to Kacy Daley and Meredith B.

   public isDVMToolAdmin = false
   public isDVM = false
   public isRM = false
   public isHM = false
   public isOpsVP = false
   public isOpsDirector = false
   public isCOS = false
   public isCOSA = false
   public isCorpVP = false
   public isAreaManager = false

   public userReview: UserReview = null
   public reviewPlan: ReviewPlan = {}

   public userReviews: UserReview[] = []

   selectedCommunicationPreferences: string[] = []
   selectedYearsExperience: string | null = null
   selectedLendAHand: boolean | null = null
   emailInputValue: string = ''
   phoneNumberInputValue: string = ''
   selectedLeaderAspirations: boolean | null = null

   medicalInterestOptions: IDropdownOption[] = []
   medicalInterests: IDropdownOption[] = []
   medicalExpertise: IDropdownOption[] = []
   educationOptions: IDropdownOption[] = []
   selectedUniversity: IDropdownOption = {} as IDropdownOption
   gradYearInputValue: string

   historicalVersions: IDropdownOption[] = []
   formVersion: IDropdownOption = {} as IDropdownOption
   lastUpdatedDate: string

   plans: UserReview[]
   selectedOptions: DVMToolSelectedOption[]

   profileValidationErrors = new Set<string>()
   private readonly emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
   private readonly phonePatternDash = /^[0-9]{3}-[0-9]{3}-[0-9]{4}$/
   private readonly phonePatternPlain = /^[0-9]{3}[0-9]{3}[0-9]{4}$/

   // Board Certification
   isBoardCertified: boolean | null = null
   boardCertificationName: string = ''

   YoYResults: any[] = []
   isLoadingYoY = false

   // Submission tracking
   isDVMSubmitted: boolean = false
   isCOSSubmitted: boolean = false
   finalFeedback: string = ''
   submittedUserId: string = ''
   submittedUserName: string = ''
   isSubmittingUpdate = false

   // Follow-up
   followUpRequired: boolean = false
   followUpNotes: string = ''
   followUpBy: string = ''
   followUpByOther: string = ''
   followUpTimeframe: string = ''
   followUpTimeframeOpen: string = ''

   mode: 'reviewee' | 'reviewer' | 'both' | null = null
   isReviewTypeEnabled = false

   // User role check
   isReviewer: boolean = false
   isPrimaryReviewer: boolean = false
   isPrimaryReviewee: boolean = false

   newRM?: string
   newCOS?: string
   newCOSA?: string
   newDO?: string
   newVP?: string
   newHM?: string

   additionalResponsibilities: any[] = []
   includeCareerExploration = false

   private autosaveTimer: any
   isAutosaving = false
   lastAutosavedAt: Date | null = null
   showAutosaveToast = false
   private autosaveToastTimer: any
   private lastAutosaveToastAt = 0

   constructor(
      private userReviewClient: UserReviewClient,
      private route: ActivatedRoute,
      private authMgr: AuthManager,
      private router: Router,
      private toastr: ToastrService,
      private dialog: MatDialog,
      private cdr: ChangeDetectorRef,
      private http: HttpClient
   ) {
      const navState = this.router.getCurrentNavigation()?.extras.state
      if (navState) {
         this.lastUpdatedDate = navState['lastUpdated']
      }

      this.authMgr.getCurrentUser().subscribe((user) => {
         this.loggedInUser = user.displayName
         this.loggedInUserId = user.id

         this.isDVMToolAdmin = this.authMgr.canAccess(user, {
            application: ApplicationArea.DVMTool,
            roleName: ['DVMToolAdmin'],
         })

         const cDVM = this.authMgr.canAccess(user, {
            application: ApplicationArea.DVMTool,
            roleName: ['DVMTool'],
         })

         const cRM = this.authMgr.canAccess(user, {
            application: ApplicationArea.DVMTool,
            roleName: ['RM'],
         })

         const cHM = this.authMgr.canAccess(user, {
            application: ApplicationArea.DVMTool,
            roleName: ['HospitalManager'],
         })

         const cOpsVP = this.authMgr.canAccess(user, {
            application: ApplicationArea.DVMTool,
            roleName: ['OpsVP'],
         })

         const cOpsDirector = this.authMgr.canAccess(user, {
            application: ApplicationArea.DVMTool,
            roleName: ['OpsDirector'],
         })

         this.isRM = user.dayforceJobCode === 'OPS-RM'
         this.isHM = user.dayforceJobCode === 'HM' || user.dayforceJobCode === 'AREA_MANAGER'
         this.isCOSA = user.dayforceJobCode === 'COSA'
         this.isCOS = user.dayforceJobCode === 'VET-COS'
         this.isOpsVP = user.dayforceJobCode === 'OPS-SVP' || user.dayforceJobCode === 'OPS-VP' || cOpsVP
         this.isCorpVP = user.dayforceJobCode === 'CORP-VP'
         this.isOpsDirector = user.dayforceJobCode === 'CORP-DIR' || cOpsDirector
         this.isDVM = user.dayforceJobCode === 'VET' || cDVM
         this.isAreaManager = user.dayforceJobCode === 'AREA_MANAGER'

         this.loggedInUserDF = user.dayforceJobCode

         const jobDesc = JOB_DESCRIPTIONS[user.dayforceJobCode]
         const jobURL = jobDesc?.url ?? ''

         const jobDescText = jobDesc ? jobDesc.label + ' Job Description' : 'Job Description Coming Soon...'

         this.reviewTemplate_CLINICAL = resolveTemplateTokens_clinical(CLINICAL_REVIEW_TEMPLATE, {
            JOB_DESC_URL: jobURL,
            JOB_DESC_TEXT: jobDescText,
         })
         /* this.reviewTemplate_CROSSTRAINED = resolveTemplateTokens_crosstrained(CROSS_TRAINED_REVIEW_TEMPLATE, {
            JOB_DESC_URL: jobURL,
            JOB_DESC_TEXT: jobDescText,
         })*/

         this.reviewTemplate_RECEPTION = resolveTemplateTokens_reception(RECEPTION_REVIEW_TEMPLATE, {
            JOB_DESC_URL: jobURL,
            JOB_DESC_TEXT: jobDescText,
         })
      })
   }

   ngOnInit(): void {
      this.revieweeId = this.route.snapshot.paramMap.get('revieweeId')

      this.route.queryParams.subscribe((params) => {
         if (params['rt'] !== undefined && params['rt'] !== null && params['rt'] !== '') {
            this.reviewType = params['rt']
            this.checkIfReviewTypeEnabled()
            this.loadReviewAttachments()
            if (this.reviewType == 'DVM' || this.reviewType == 'DO') {
               this.isForDVM = true
            }
         }
         if (!params['rt']) {
            this.reviewTypeSet = false
         }
      })

      if (this.revieweeId === 'new') {
         this.route.queryParams.subscribe((params) => {
            const revieweeUserId = params['userId']

            if (revieweeUserId) {
               this.startNewPlanForUser(revieweeUserId)
            }
         })
      } else {
         this.getUserReviewsForReviewee(Number(this.revieweeId))
      }

      setTimeout(() => {
         this.populateDropDowns()
      }, 200)

      this.checkIfUserIsReviewer()
      this.setCrosstrainedJobs()
      this.isUnderReview = true

      window.addEventListener('scroll', this.onScroll.bind(this))
   }

   checkIfReviewTypeEnabled(): void {
      if (this.reviewType === 'DVM') {
         this.isReviewTypeEnabled = true
         return
      }

      if (this.userReview?.cycle == 'OPEN') {
         this.isReviewTypeEnabled = true
         return
      }

      this.userReviewClient.isCycleEnabled(this.reviewType).subscribe({
         next: (isEnabled) => (this.isReviewTypeEnabled = isEnabled),
         error: () => (this.isReviewTypeEnabled = false),
      })
   }

   setCrosstrainedJobs(): void {
      ;(this.userReviewClient as any).getJobDescriptions(this.revieweeId, 'CROSSTRAINED').subscribe({
         next: async (response: any) => {
            let labels: string[] = []
            try {
               const raw = response?.data?.text ? JSON.parse(await response.data.text()) : response
               labels = Array.isArray(raw) ? raw : []
            } catch {
               labels = []
            }

            const jobDescText = labels[0] ?? JOB_DESCRIPTIONS[this.loggedInUserDF]?.label
            const jobURL = this.getJobDescUrlByLabel(jobDescText) ?? JOB_DESCRIPTIONS[this.loggedInUserDF]?.url

            const jobDescText2 = labels[1] ?? ''
            const jobURL2 = jobDescText2 ? (this.getJobDescUrlByLabel(jobDescText2) ?? '') : ''

            this.reviewTemplate_CROSSTRAINED = resolveTemplateTokens_crosstrained(CROSS_TRAINED_REVIEW_TEMPLATE, {
               JOB_DESC_URL: jobURL,
               JOB_DESC_TEXT: jobDescText ? jobDescText + ' Job Description' : 'Job Description Not Available',
               JOB_DESC_URL_2: jobURL2,
               JOB_DESC_TEXT_2: jobDescText2 ? jobDescText2 + ' Job Description' : 'Job Description Not Available',
            })
         },
         error: () => {
            this.reviewTemplate_CROSSTRAINED = resolveTemplateTokens_crosstrained(CROSS_TRAINED_REVIEW_TEMPLATE, {
               JOB_DESC_URL: '',
               JOB_DESC_TEXT: '',
               JOB_DESC_URL_2: '',
               JOB_DESC_TEXT_2: '',
            })
         },
      })
   }

   getJobDescUrlByLabel(label: string): string | undefined {
      return Object.values(JOB_DESCRIPTIONS).find((d) => d.label === label)?.url
   }

   checkIfUserIsReviewer(): void {
      this.authMgr.getCurrentUser().subscribe((user) => {
         this.isReviewer = false
         this.isPrimaryReviewer = false
         this.isPrimaryReviewee = false // Reset

         // Check if current user is the reviewee
         if (this.reviewPlan?.revieweeUserId && user.id === Number(this.reviewPlan.revieweeUserId)) {
            this.isPrimaryReviewee = true
         }

         // DVM is reviewee, COS is reviewer
         if (this.reviewType === 'DVM') {
            if (
               (this.isCOS && user.id === Number(this.reviewPlan.cosUserId)) ||
               user.id === Number(this.userReview?.cosUserId) ||
               (this.isCOS && user.displayName === this.reviewPlan.cosDisplayName) ||
               (this.isCOS && user.displayName === this.newCOS) ||
               (this.isCOSA && user.id === Number(this.reviewPlan.vetLeaderUserId)) ||
               (this.isCOSA && user.displayName === this.newCOSA) ||
               (this.isRM && (user.id === Number(this.reviewPlan.rmUserId) || user.displayName === this.userReview?.rmName)) ||
               (this.isOpsVP && user.id === Number(this.reviewPlan.vpUserId)) ||
               (this.isOpsDirector && user.id === Number(this.reviewPlan.directorUserId)) ||
               this.isCorpVP
            ) {
               this.isReviewer = true
               this.isPrimaryReviewer = true
            } else if (this.isHM || this.isAreaManager) {
               this.isReviewer = true
            }
         }

         // HM is reviewee, RM is reviewer
         else if (this.reviewType === 'HM') {
            if (
               user.id === Number(this.reviewPlan.rmUserId) ||
               user.displayName === this.userReview?.rmName ||
               (this.isOpsDirector && user.id === Number(this.reviewPlan.directorUserId)) ||
               (this.isOpsVP && user.id === Number(this.reviewPlan.vpUserId)) ||
               (this.isCOSA && user.id === Number(this.reviewPlan.vetLeaderUserId))
            ) {
               this.isReviewer = true
               this.isPrimaryReviewer = true
            } else if (this.isCorpVP && this.isDVMSubmitted && this.isCOSSubmitted) {
               this.isReviewer = true
               this.isPrimaryReviewer = false
            }
         }

         // COS is reviewee, COSA or RM are reviewers
         else if (this.reviewType === 'COS') {
            if (
               (this.isCOSA && user.id === Number(this.reviewPlan.vetLeaderUserId)) ||
               (this.isRM &&
                  (user.id === Number(this.reviewPlan.rmUserId) ||
                     user.displayName === this.reviewPlan.rmDisplayName ||
                     user.displayName === this.userReview?.rmName)) ||
               this.isCorpVP ||
               (this.isOpsVP && user.id === Number(this.reviewPlan.vpUserId)) ||
               (this.isOpsDirector && user.id === Number(this.reviewPlan.directorUserId))
            ) {
               this.isReviewer = true
               this.isPrimaryReviewer = true
            }
         }

         // RM is reviewee, DO is reviewer
         else if (this.reviewType === 'RM') {
            if (
               (this.isOpsDirector && user.id === Number(this.reviewPlan?.directorUserId)) ||
               (this.isOpsVP && user.id === Number(this.reviewPlan.vpUserId))
            ) {
               this.isReviewer = true
               this.isPrimaryReviewer = true
            }
         }

         // DO is reviewee, VP is reviewer
         else if (this.reviewType === 'DO') {
            if (this.isOpsVP && user.id === Number(this.reviewPlan.vpUserId)) {
               this.isReviewer = true
               this.isPrimaryReviewer = true
            }
         }

         // Any TEAM member is reviewee, HM is reviewer
         else if (this.reviewType === 'CLINICAL' || this.reviewType === 'CROSSTRAINED' || this.reviewType === 'RECEPTION') {
            if (
               (this.isHM && user.id === Number(this.reviewPlan.hmUserId)) ||
               (this.isHM && this.loggedInUser == this.newHM) ||
               this.loggedInUser == this.userReview?.hmName ||
			   this.loggedInUser == this.userReview?.hmName2 ||
               (this.isCOS && user.id === Number(this.reviewPlan.cosUserId)) ||
               user.id === Number(this.userReview?.cosUserId) ||
               (this.isCOS && user.displayName === this.reviewPlan.cosDisplayName) ||
               (this.isCOS && user.displayName === this.newCOS) ||
               (this.isCOSA && user.id === Number(this.reviewPlan.vetLeaderUserId)) ||
               (this.isCOSA && user.displayName === this.newCOSA) ||
               (this.isRM && (user.id === Number(this.reviewPlan.rmUserId) || user.displayName === this.userReview?.rmName)) ||
               (this.isOpsVP && user.id === Number(this.reviewPlan.vpUserId)) ||
               (this.isOpsDirector && user.id === Number(this.reviewPlan.directorUserId)) ||
               this.isCorpVP ||
               this.isAreaManager
            ) {
               this.isReviewer = true
               this.isPrimaryReviewer = true
            }
         }

         if (user.id === Number(this.userReview?.reviewerUserId)) {
            this.isReviewer = true
            this.isPrimaryReviewer = true
         }
      })

      if (this.isOpsVP) {
         this.isReviewer = true
      }

      if (this.isOpsDirector && this.reviewType != 'DO') {
         this.isPrimaryReviewer = true
         this.isReviewer = true
      }
   }

   getRevieweeLabel(short: boolean = false): string {
      switch (this.reviewType) {
         case 'HM':
            return short ? 'HM' : 'Hospital Manager'
         case 'COS':
            return short ? 'COS' : 'Chief of Staff'
         case 'RM':
            return short ? 'RM' : 'Regional Manager'
         case 'DO':
            return short ? 'DO' : 'Director of Operations'
         case 'RECEPTION':
            return short ? 'Team Member' : 'Reception Team Member'
         case 'CROSSTRAINED':
            return short ? 'Team Member' : 'Cross-Trained Team Member'
         case 'CLINICAL':
            return short ? 'Team Member' : 'Clinical Team Member'
         case 'DVM':
         default:
            return short ? 'aDVM' : 'Associate DVM'
      }
   }

   getReviewerLabel(short: boolean = false): string {
      switch (this.reviewType) {
         case 'HM':
            return short ? 'RM' : 'Regional Manager'
         case 'COS':
            return short ? 'COSA/RM' : 'Chief of Staff Advisor'
         case 'RM':
            return short ? 'DO' : 'Director of Operations'
         case 'DO':
            return short ? 'VP' : 'Vice President of Operations'
         case 'RECEPTION':
            return short ? 'HM' : 'Reception Hospital Manager'
         case 'CROSSTRAINED':
            return short ? 'HM' : 'Cross-Trained Hospital Manager'
         case 'CLINICAL':
            return short ? 'HM' : 'Clinical Hospital Manager'
         case 'DVM':
         default:
            return short ? 'COS' : 'Chief of Staff'
      }
   }

   getReviewerName(userReview: any): string {
      switch (this.reviewType) {
         case 'HM':
            return userReview.rmName
         case 'COS':
            return userReview.vetLeaderName
         case 'RM':
            return userReview.directorName
         case 'DO':
            return userReview.vpName
         case 'RECEPTION':
            return userReview.hmName
         case 'CROSSTRAINED':
            return userReview.hmName
         case 'CLINICAL':
            return userReview.hmName
         case 'DVM':
         default:
            return userReview.cosName
      }
   }

   getReviewTemplate() {
      if (this.reviewType == 'DVM') {
         return this.reviewTemplate_DVM
      } else if (this.reviewType == 'HM') {
         return this.reviewTemplate_HM
      } else if (this.reviewType == 'COS') {
         return this.reviewTemplate_COS
      } else if (this.reviewType == 'DO') {
         return this.reviewTemplate_DO
      } else if (this.reviewType == 'RM') {
         return this.reviewTemplate_RM
      } else if (this.reviewType == 'RECEPTION') {
         return this.reviewTemplate_RECEPTION
      } else if (this.reviewType == 'CROSSTRAINED') {
         return this.reviewTemplate_CROSSTRAINED
      } else if (this.reviewType == 'CLINICAL') {
         return this.reviewTemplate_CLINICAL
      }
   }

   onScroll(): void {
      this.showBackToTop = window.pageYOffset > 300
   }

   scrollToTop(): void {
      window.scrollTo({ top: 0, behavior: 'smooth' })
   }

   onRevieweeDataChanged(changes: Partial<UserReview>) {
      // Merge the changes from the reviewee child component into the main userReview
      Object.assign(this.userReview, changes)
      this.scheduleAutosave()
   }

   onReviewerDataChanged(changes: Partial<UserReview>) {
      // Merge the changes from the COS child component into the main userReview
      Object.assign(this.userReview, changes)
      this.scheduleAutosave()
   }

   private scheduleAutosave(): void {
      if (this.isFormReadOnly() || this.userReview?.isCompleted) return

      clearTimeout(this.autosaveTimer)

      this.autosaveTimer = setTimeout(() => {
         this.autosaveReview()
      }, 1500)
   }

   private autosaveReview(): void {
      if (this.isSubmittingUpdate || this.isAutosaving) return
      if (this.isFormReadOnly() || this.userReview?.isCompleted) return

      this.isAutosaving = true

      this.saveUserReview(false, false, true)
   }

   public startNewPlanForUser(revieweeId: number) {
      this.userReviewClient.getUser(revieweeId, this.reviewType).subscribe({
         next: (data) => {
            if (!data) {
               this.toastr.error('Unable to load review plan. User not found.')
            }

            this.reviewPlan = data

            const getMainReviewer =
               this.reviewType == 'DVM'
                  ? this.reviewPlan?.cosUserId
                     ? this.reviewPlan?.cosUserId
                     : this.reviewPlan?.vetLeaderUserId
                  : this.reviewType == 'HM'
                    ? this.reviewPlan?.rmUserId
                    : this.reviewType == 'COS'
                      ? this.reviewPlan?.rmUserId
                      : this.reviewType == 'RM'
                        ? this.reviewPlan?.directorUserId
                        : this.reviewType == 'DO'
                          ? this.reviewPlan?.vpUserId
                          : this.reviewType == 'RECEPTION' || this.reviewType == 'CROSSTRAINED' || this.reviewType == 'CLINICAL'
                            ? this.reviewPlan?.hmUserId
                            : ''

            this.userReview = {
               revieweeName: this.reviewPlan?.revieweeDisplayName,
               revieweeId: this.reviewPlan?.revieweeUserId,
               reviewerUserId: getMainReviewer,
               cosUserId: this.reviewPlan?.cosDisplayName ? this.reviewPlan?.cosUserId : this.reviewPlan?.vetLeaderUserId,
               practiceName: this.reviewPlan?.practiceName,
               practiceSKey: this.reviewPlan?.practiceSKey,
               rmName: this.reviewPlan?.rmDisplayName,
               rmUserId: this.reviewPlan?.rmUserId,
               hmName: this.reviewPlan?.hmDisplayName,
			   hmName2: this.reviewPlan?.hmDisplayName2,
               cosName: this.reviewPlan?.cosDisplayName,
               vetLeaderName: this.reviewPlan?.vetLeaderDisplayName,
               directorName: this.reviewPlan?.directorDisplayName,
               vpName: this.reviewPlan?.vpDisplayName,
               dateHired: this.reviewPlan?.hireDate,
               communicationPreference: [],

               isBoardCertified: null,
               boardCertificationName: '',
               isSubmitted: false,

               // SUBMISSION TRACKING PROPERTIES:
               isDVMSubmitted: false,
               isCOSSubmitted: false,
               dvmSubmittedOn: null,
               cosSubmittedOn: null,

               // FINAL FEEDBACK PROPERTY:
               finalFeedback: '',
            }

            if (this.reviewPlan?.practiceSKey) {
               this.loadYoY(this.reviewPlan.practiceSKey)
            }
            this.checkIfUserIsReviewer()
            this.allSectionsLoaded = true
         },
         error: (error) => {
            console.error('Error fetching data:', error)
            this.toastr.error('Unable to load review plan. Please try again.')
            //this.router.navigate(['/performance-hub/reviews'], { queryParamsHandling: 'preserve' })
         },
      })
   }

   public getUserReviewsForReviewee(revieweeId: number) {
      forkJoin({
         plans: this.userReviewClient.getUserReviewsForReviewee(revieweeId, this.reviewType),
         selectedOptions: this.userReviewClient.getDvmToolSelectedOptions(revieweeId),
         additionalResponsibilities: (this.userReviewClient as any)
            .getAdditionalResponsibilities(revieweeId, this.reviewType)
            .pipe(switchMap((response: any) => from(response.data.text()).pipe(map((text: any) => JSON.parse(text))))),
      }).subscribe({
         next: ({ plans, selectedOptions, additionalResponsibilities }: any) => {
            this.plans = plans
            this.selectedOptions = selectedOptions

            const savedRow = (additionalResponsibilities as any[])?.[0]

            this.includeCareerExploration = savedRow?.includeCareerExploration ?? true

            let responsibilityTexts: string[] = []

            if (savedRow?.responsibilityText) {
               try {
                  const parsed = JSON.parse(savedRow.responsibilityText)
                  responsibilityTexts = Array.isArray(parsed) ? parsed : [savedRow.responsibilityText]
               } catch {
                  responsibilityTexts = [savedRow.responsibilityText]
               }
            }

            this.additionalResponsibilities = responsibilityTexts.map((text, index) => ({
               id: savedRow?.id ? `${savedRow.id}_${index + 1}` : index + 1,
               revieweeUserId: savedRow?.revieweeUserId ?? revieweeId,
               reviewType: savedRow?.reviewType ?? this.reviewType,
               responsibilityText: text,
               displayOrder: index + 1,
            }))

            this.loadUserReview()
            this.checkIfUserIsReviewer()
            this.checkIfReviewTypeEnabled()
         },
         error: (err) => {
            console.error('Error fetching review data:', err)
            this.allSectionsLoaded = true
         },
      })
   }

   public loadUserReview() {
      this.userReviews = this.plans

      if (this.plans?.length > 0) {
         this.userReview =
            this.plans
               .sort((a, b) => dayjs.utc(b.dateUpdated).local().toDate().getTime() - dayjs.utc(a.dateUpdated).local().toDate().getTime())
               .find(
                  (plan) =>
                     dayjs.utc(plan.dateUpdated).local().format('MM/DD/YYYY') + ' ' + dayjs.utc(plan.dateUpdated).local().format('hh:mm A') ===
                     dayjs.utc(this.lastUpdatedDate).local().format('MM/DD/YYYY') + ' ' + dayjs.utc(this.lastUpdatedDate).local().format('hh:mm A')
               ) ?? this.plans[0]

         const userReviewId = this.userReview?.id

         if (userReviewId) {
            // Load the full review + responses
            forkJoin({
               reviewData: this.userReviewClient.getUserReview(userReviewId),
               selectedOptions: this.userReviewClient.getDvmToolSelectedOptions(userReviewId),
               profileData: this.userReviewClient.getUser(Number(this.revieweeId), this.reviewType),
            }).subscribe({
               next: ({ reviewData, selectedOptions, profileData }) => {
                  // Extract plan and responses
                  let plan: UserReview
                  let responses: any[] = []

                  if ('plan' in reviewData) {
                     plan = reviewData.plan
                     responses = reviewData.responses ?? []
                  } else {
                     plan = reviewData as UserReview
                  }

                  // First, load the review data
                  this.userReview = plan

                  // Bind UI fields
                  const p2 = this.userReview

                  this.emailInputValue = p2?.dvmEmail ?? ''
                  this.phoneNumberInputValue = p2?.dvmPhone ?? ''
                  this.isBoardCertified = p2?.isBoardCertified ?? null
                  this.boardCertificationName = p2?.boardCertificationName ?? ''
                  this.selectedYearsExperience = p2?.yearsExperience != null ? String(p2.yearsExperience) : null
                  this.selectedLendAHand = p2?.lendAHandInterest ?? null
                  this.selectedLeaderAspirations = p2?.leaderAsprations ?? null
                  this.gradYearInputValue = p2?.gradYear ?? ''
                  this.selectedCommunicationPreferences = p2?.communicationPreference ?? []
                  this.reviewType = p2?.reviewType ?? this.reviewType

                  // Submission tracking
                  this.isDVMSubmitted = p2?.isDVMSubmitted ?? false
                  this.isCOSSubmitted = p2?.isCOSSubmitted ?? false
                  this.finalFeedback = p2?.finalFeedback ?? ''

                  // Follow-up
                  this.followUpRequired = p2?.followUpRequired ?? false
                  this.followUpNotes = p2?.followUpNotes ?? ''
                  this.followUpBy = p2?.followUpBy ?? ''
                  this.followUpByOther = p2?.followUpByOther ?? ''
                  this.followUpTimeframe = p2?.followUpTimeframe ?? ''
                  this.followUpTimeframeOpen = p2?.followUpTimeframeOpen ?? ''

                  // Override with fresh profile data from getUser
                  this.reviewPlan = profileData

                  if (this.reviewPlan) {
                     this.reviewPlan.deferralStatus = this.reviewPlan.deferralStatus
                  }

                  const getMainReviewer = this.userReview?.reviewerUserId
                     ? this.userReview?.reviewerUserId
                     : this.reviewType == 'DVM'
                       ? this.reviewPlan?.cosUserId
                          ? this.reviewPlan?.cosUserId
                          : this.reviewPlan?.vetLeaderUserId
                       : this.reviewType == 'HM'
                         ? this.reviewPlan?.rmUserId
                         : this.reviewType == 'COS'
                           ? this.reviewPlan?.rmUserId
                           : this.reviewType == 'RM'
                             ? this.reviewPlan?.directorUserId
                             : this.reviewType == 'DO'
                               ? this.reviewPlan?.vpUserId
                               : this.reviewType == 'RECEPTION' || this.reviewType == 'CROSSTRAINED' || this.reviewType == 'CLINICAL'
                                 ? this.reviewPlan?.hmUserId
                                 : ''

                  this.userReview = {
                     ...this.userReview,
                     revieweeName: profileData?.revieweeDisplayName,
                     revieweeId: profileData?.revieweeUserId?.toString(),
                     reviewerUserId: getMainReviewer,
                     cosUserId: plan.cosUserId,
                     practiceName: profileData?.practiceName ? profileData?.practiceName : plan?.practiceName,
                     practiceSKey: profileData?.practiceSKey ? profileData?.practiceSKey : plan?.practiceSKey,

                     rmName: profileData?.rmDisplayName || plan.rmName,
                     rmUserId: profileData?.rmUserId || plan.rmUserId,
                     hmName: profileData?.hmDisplayName || plan.hmName,
					 hmName2: profileData?.hmDisplayName2 || plan.hmName2,

                     cosName: profileData?.cosDisplayName || plan.cosName,

                     vetLeaderName: profileData?.vetLeaderDisplayName || plan.vetLeaderName,
                     directorName: profileData?.directorDisplayName || plan.directorName,
                     vpName: profileData?.vpDisplayName || plan.vpName,

                     cycle: this.reviewType == 'DVM' && !plan.cycle ? 'Q3' : plan.cycle,
                  }

                  this.newRM = this.reviewPlan?.rmDisplayName == plan?.rmName ? '' : plan?.rmName
                  this.newCOSA = this.reviewPlan?.vetLeaderDisplayName == plan?.vetLeaderName ? '' : plan?.vetLeaderName
                  this.newCOS = this.reviewPlan?.cosDisplayName == plan?.cosName ? '' : plan?.cosName
                  this.newDO = this.reviewPlan?.directorDisplayName == plan?.directorName ? '' : plan?.directorName
                  this.newVP = this.reviewPlan?.vpDisplayName == plan?.vpName ? '' : plan?.vpName
                  this.newHM = this.reviewPlan?.hmDisplayName == plan?.hmName ? '' : plan?.hmName

                  this.submittedUserId = plan.submittedUserId
                  this.submittedUserName = plan.submittedUserName

                  if (this.performanceHubAdmin.includes(Number(this.submittedUserId))) {
                     this.submittedByAdmin = true
                  }

                  // Load medical interests/university
                  this.selectedOptions = selectedOptions ?? []
                  this.loadMedicalInterests(userReviewId, this.selectedOptions)
                  this.loadSelectedUniversity(userReviewId, this.selectedOptions)

                  // Hydrate responses
                  if (responses.length > 0) {
                     setTimeout(() => {
                        this.revieweeSections?.hydrateFromResponses(responses, 'reviewee')
                        this.reviewerSections?.hydrateFromResponses(responses, 'reviewer')
                     }, 100)
                  }

                  // Load YoY if needed
                  if (plan.practiceSKey) {
                     this.loadYoY(plan.practiceSKey)
                  }

                  // Update reviewer status
                  this.checkIfUserIsReviewer()

                  // Now show the UI
                  this.allSectionsLoaded = true

                  setTimeout(() => {
                     if (this.isReviewer && this.isDVMSubmitted && this.isCOSSubmitted && !this.userReview?.isCompleted) {
                        document.getElementById('final-feedback-section')?.scrollIntoView({
                           behavior: 'smooth',
                           block: 'start',
                        })
                        this.toastr.info(
                           'Both the ' +
                              this.getRevieweeLabel() +
                              ' and ' +
                              this.getReviewerLabel() +
                              ' have submitted their reviews.  Please add final feedback below to complete.',
                           '',
                           {
                              timeOut: 6500,
                              positionClass: 'toast-center-center',
                           }
                        )
                     }
                  }, 500)
               },
               error: (err) => {
                  console.error('Failed to load review data', err)
                  this.allSectionsLoaded = true // Show even on error
               },
            })
         }
      } else {
         this.startNewPlanForUser(Number(this.revieweeId))
      }
   }

   public loadMedicalInterests(revieweeId: number, selectedOptions: DVMToolSelectedOption[]) {
      const medicalInterestDropdownOptions = selectedOptions.filter(
         (x) => x.dvmEngagementId === revieweeId && x.dvmToolSelection.dvmToolSelectionTypeID === DVMToolSelectionType.MedicalInterests
      )
      this.medicalInterests = medicalInterestDropdownOptions
         .filter((option) => option?.medExpertise !== true) // treat null/0 as "interests"
         .map((option) => ({
            id: option.dvmToolSelectionId,
            name: option.dvmToolSelection.description,
            medExpertise: false,
         }))

      this.medicalExpertise = medicalInterestDropdownOptions
         .filter((option) => option?.medExpertise === true)
         .map((option) => ({
            id: option.dvmToolSelectionId,
            name: option.dvmToolSelection.description,
            medExpertise: true,
         }))
   }

   public loadSelectedUniversity(revieweeId: number, selectedOptions: DVMToolSelectedOption[]) {
      this.selectedUniversity = selectedOptions
         .filter((x) => x.dvmEngagementId === revieweeId && x.dvmToolSelection.dvmToolSelectionTypeID === DVMToolSelectionType.Education)
         .map((option) => {
            return { id: option.dvmToolSelectionId, name: option.dvmToolSelection.description }
         })[0]
   }

   getInitials(name: string | undefined | null): string {
      if (!name) return ''

      const words = name.trim().split(' ')
      const initials = words.map((word) => word.charAt(0).toUpperCase()).join('')

      return initials
   }

   // --- Defer ---
   isUserDeferred(): boolean {
      if (this.userReview?.cycle == 'OPEN') {
         return false
      }
      return (
         !!this.reviewPlan?.isDeferred &&
         this.reviewPlan.deferralStatus != 'Pending' &&
         this.reviewPlan.deferralStatus != 'Rejected' &&
         this.reviewPlan.deferralStatus != 'ReEnabled'
      )
   }

   public saveUserReview(isSubmit: boolean = false, isComplete: boolean = false, isAutosave: boolean = false) {
      this.isSubmittingUpdate = !isAutosave
      this.cdr.detectChanges()

      const missingFields: string[] = []

      // COMPLETE: No validation, just save
      if (isComplete) {
         // intentionally skip validation
         this.mode = this.isReviewer ? 'reviewer' : 'reviewee'
      }
      // SUBMIT: Full validation required
      else if (isSubmit) {
         this.mode = this.isReviewer ? 'reviewer' : 'reviewee'

         if (!this.submittedByAdminClicked && !this.submittedByAdminReviewerClicked) {
            // Only validate profile if reviewType is DVM and user is the primary reviewee
            if (this.reviewType === 'DVM' && this.isPrimaryReviewee) {
               missingFields.push(...this.validateProfileRequired())
            }

            if (this.isPrimaryReviewee) {
               const revieweeResult = this.revieweeSections?.validateRequired('reviewee')
               if (revieweeResult && !revieweeResult.ok) missingFields.push(...revieweeResult.missingFields)
            } else {
               const reviewerResult = this.reviewerSections?.validateRequired('reviewer')
               if (reviewerResult && !reviewerResult.ok) missingFields.push(...reviewerResult.missingFields)
            }

            if (missingFields.length) {
               this.toastr.error('Please fill out all required fields before submitting.')
               setTimeout(() => this.scrollToFirstMissing(missingFields[0]), 0)
               this.isSubmittingUpdate = false
               return
            }
         }
      }
      // DRAFT: Only validate email
      else {
         this.mode = null

         // Autosave should NOT validate profile fields or scroll the user
         if (
            !isAutosave &&
            this.reviewType == 'DVM' &&
            this.isPrimaryReviewee &&
            (!this.emailInputValue || !this.emailPattern.test(this.emailInputValue))
         ) {
            this.toastr.error('Please fill out your email address before saving.')
            setTimeout(() => this.scrollToFirstMissing('profile:email'), 0)
            this.isSubmittingUpdate = false
            return
         }
      }

      // Build PerformanceReviews
      const now = new Date()

      const userReviewToSave: UserReview = { ...this.userReview }

      userReviewToSave.dateUpdated = dayjs.utc(now).local().toDate()

      // profile fields
      userReviewToSave.communicationPreference = this.selectedCommunicationPreferences
      userReviewToSave.dvmEmail = this.emailInputValue
      userReviewToSave.dvmPhone = this.phoneNumberInputValue
      userReviewToSave.yearsExperience = this.selectedYearsExperience != null ? Number(this.selectedYearsExperience) : null
      userReviewToSave.lendAHandInterest = this.selectedLendAHand
      userReviewToSave.leaderAsprations = this.selectedLeaderAspirations
      userReviewToSave.gradYear = this.gradYearInputValue
      userReviewToSave.isBoardCertified = this.isBoardCertified
      userReviewToSave.boardCertificationName = this.boardCertificationName
      userReviewToSave.reviewType = this.reviewType

      // submission tracking
      if (isSubmit) {
         if ((this.isReviewer || this.submittedByAdminReviewer) && !this.submittedByAdmin) {
            userReviewToSave.isCOSSubmitted = true
            userReviewToSave.cosSubmittedOn = now
         } else {
            userReviewToSave.isDVMSubmitted = true
            userReviewToSave.dvmSubmittedOn = now
         }
         userReviewToSave.submittedUserId = String(this.loggedInUserId)
         userReviewToSave.submittedUserName = this.loggedInUser
      }

      if (isComplete) {
         userReviewToSave.isCompleted = true
         userReviewToSave.completedOn = now
         userReviewToSave.finalFeedback = this.finalFeedback
         userReviewToSave.followUpRequired = this.followUpRequired
         userReviewToSave.followUpNotes = this.followUpRequired ? this.followUpNotes : ''
         userReviewToSave.followUpBy = this.followUpRequired ? this.followUpBy : ''
         userReviewToSave.followUpByOther = this.followUpRequired && this.followUpBy === 'Other' ? this.followUpByOther : ''
         userReviewToSave.followUpTimeframe = this.followUpRequired ? this.followUpTimeframe : ''
         userReviewToSave.followUpTimeframeOpen = this.followUpRequired && this.followUpTimeframe === 'Open' ? this.followUpTimeframeOpen : ''
         userReviewToSave.submittedUserId = String(this.loggedInUserId)
         userReviewToSave.submittedUserName = this.loggedInUser

         if (this.reviewType === 'DVM' && userReviewToSave.dateHired) {
            const month = new Date(userReviewToSave.dateHired).getUTCMonth() + 1
            userReviewToSave.cycle = month <= 3 ? 'Q1' : month <= 6 ? 'Q2' : month <= 9 ? 'Q3' : 'Q4'
         } else {
            userReviewToSave.cycle = ''
            userReviewToSave.whenOpenCompletedBy = ''
         }
      }

      let responses: any[] = []

      if (this.isReviewer) {
         responses = this.reviewerSections?.exportResponses('reviewer') ?? []
      } else {
         responses = this.revieweeSections?.exportResponses('reviewee') ?? []
      }

      const request = {
         plan: userReviewToSave,
         responses: responses,
      }

      // Save
      this.userReviewClient
         .saveUserReview(request)
         .pipe(
            switchMap((reviewId: number) => {
               if (!isAutosave) {
                  this.toastr.success(
                     isComplete
                        ? 'Your form has been completed.'
                        : isSubmit
                          ? 'Your form was submitted successfully.'
                          : 'Your form was saved successfully.'
                  )
               }

               const saveDropdownOptionsRequest: DVMToolSelectedOption[] = [
                  ...this.medicalInterests
                     .filter((x) => x)
                     .map((option) => {
                        return { dvmEngagementId: reviewId, dvmToolSelectionId: option.id, medExpertise: false }
                     }),
                  ...this.medicalExpertise
                     .filter((x) => x)
                     .map((option) => {
                        return { dvmEngagementId: reviewId, dvmToolSelectionId: option.id, medExpertise: true }
                     }),
               ]

               if (this.selectedUniversity?.id > 0) {
                  saveDropdownOptionsRequest.push({ dvmEngagementId: reviewId, dvmToolSelectionId: this.selectedUniversity.id, medExpertise: false })
               }

               return forkJoin({
                  reviewId: of(reviewId),
                  dropdowns: this.userReviewClient.saveDvmToolSelectionOptions(saveDropdownOptionsRequest),
               })
            })
         )
         .subscribe({
            next: ({ reviewId }) => {
               this.lastUpdatedDate =
                  dayjs.utc(userReviewToSave.dateUpdated).local().format('MM/DD/YYYY') +
                  ' ' +
                  dayjs.utc(userReviewToSave.dateUpdated).local().format('hh:mm A')

               this.isSubmittingUpdate = false
               this.submittedByAdminClicked = false
               this.submittedByAdminReviewerClicked = false
               this.cdr.detectChanges()
               this.userReview.id = reviewId
               this.userReview.dateUpdated = userReviewToSave.dateUpdated

               this.isAutosaving = false
               this.lastAutosavedAt = new Date()

               if (isAutosave) {
                  this.maybeShowAutosaveToast()
               } else {
                  this.getUserReviewsForReviewee(Number(this.revieweeId))
               }
            },
            error: (err) => {
               this.isAutosaving = false
               this.lastAutosavedAt = new Date()
               this.isSubmittingUpdate = false
               this.cdr.detectChanges()
               console.error('❌ Failed during save operation:', err)
               this.toastr.error('Save failed. Please try again.')
            },
         })
      ;(this.userReviewClient as any)
         .saveAdditionalResponsibilities({
            revieweeUserId: Number(this.revieweeId),
            reviewType: this.reviewType,
            cycle: this.userReview?.cycle ?? '',
            includeCareerExploration: this.includeCareerExploration,
            responsibilities: this.additionalResponsibilities.map((r, index) => ({
               responsibilityText: r.responsibilityText,
               displayOrder: index + 1,
               isActive: true,
            })),
         })
         .subscribe()
   }

   private maybeShowAutosaveToast(): void {
      const now = Date.now()

      // only show every 30 seconds max, so it feels subtle
      if (now - this.lastAutosaveToastAt < 30000) return

      this.lastAutosaveToastAt = now
      this.showAutosaveToast = true

      clearTimeout(this.autosaveToastTimer)

      this.autosaveToastTimer = setTimeout(() => {
         this.showAutosaveToast = false
      }, 2200)
   }

   async onClickSave(isSubmit: boolean = false, isComplete: boolean = false) {
      this.isSubmittingUpdate = true
      if (isComplete && this.isReviewer) {
         const confirmed = await this.dialog
            .open(ConfirmDialogComponent, {
               width: '520px',
               disableClose: true,
               data: {
                  title: 'Finalize this review?',
                  message: `This review should only be finalized once you have had a discussion with your ${this.getRevieweeLabel(true)}. Finalizing this review will make all ${this.getReviewerLabel(true)} comments visible to the ${this.getRevieweeLabel(true)}, and will no longer be editable by either party.`,
               },
            })
            .afterClosed()
            .toPromise()

         if (!confirmed) {
            return
         }
      }

      this.saveUserReview(isSubmit, isComplete)
   }

   private validateProfileRequired(): string[] {
      const missingFields: string[] = []

      // clear old
      this.profileValidationErrors.clear()

      // Communication Preference (must pick at least 1)
      if (!this.selectedCommunicationPreferences || this.selectedCommunicationPreferences.length === 0) {
         missingFields.push('profile:communicationPreference')
         this.profileValidationErrors.add('profile:communicationPreference')
      }

      // Email (required + format)
      if (!this.emailInputValue?.trim()) {
         missingFields.push('profile:email')
         this.profileValidationErrors.add('profile:email')
      } else if (!this.emailPattern.test(this.emailInputValue.trim())) {
         missingFields.push('profile:emailFormat')
         this.profileValidationErrors.add('profile:emailFormat')
      }

      // Phone (required + format)
      if (!this.phoneNumberInputValue?.trim()) {
         missingFields.push('profile:phone')
         this.profileValidationErrors.add('profile:phone')
      } else if (!this.phonePatternDash.test(this.phoneNumberInputValue) && !this.phonePatternPlain.test(this.phoneNumberInputValue)) {
         missingFields.push('profile:phoneFormat')
         this.profileValidationErrors.add('profile:phoneFormat')
      }

      // Vet School
      if (!this.selectedUniversity) {
         missingFields.push('profile:vetSchool')
         this.profileValidationErrors.add('profile:vetSchool')
      }

      // Grad Year
      if (!this.gradYearInputValue?.trim()) {
         missingFields.push('profile:gradYear')
         this.profileValidationErrors.add('profile:gradYear')
      }

      // Years Experience radio
      if (this.selectedYearsExperience === null || this.selectedYearsExperience === undefined || this.selectedYearsExperience === '') {
         missingFields.push('profile:yearsExperience')
         this.profileValidationErrors.add('profile:yearsExperience')
      }

      // Lend-a-hand yes/no
      if (this.selectedLendAHand === null || this.selectedLendAHand === undefined) {
         missingFields.push('profile:lendAHand')
         this.profileValidationErrors.add('profile:lendAHand')
      }

      // Board certified yes/no
      if (this.isBoardCertified === null || this.isBoardCertified === undefined) {
         missingFields.push('profile:boardCertified')
         this.profileValidationErrors.add('profile:boardCertified')
      }

      // Board certification name required only if Yes
      if (this.isBoardCertified === true && !this.boardCertificationName?.trim()) {
         missingFields.push('profile:boardCertificationName')
         this.profileValidationErrors.add('profile:boardCertificationName')
      }

      // Leadership aspirations yes/no
      if (this.selectedLeaderAspirations === null || this.selectedLeaderAspirations === undefined) {
         missingFields.push('profile:leaderAspirations')
         this.profileValidationErrors.add('profile:leaderAspirations')
      }

      // Medical Interests (required: at least 1)
      if (!this.medicalInterests || this.medicalInterests.length === 0) {
         missingFields.push('profile:medicalInterests')
         this.profileValidationErrors.add('profile:medicalInterests')
      }

      // Force change detection (Set mutation sometimes doesn’t trigger)
      this.profileValidationErrors = new Set(this.profileValidationErrors)

      return missingFields
   }

   canShowMergedView(): boolean {
      if (this.printerModalOpened) {
         return this.printReviewee && this.printReviewer ? true : false
      }

      if (this.userReview?.isCompleted) {
         return true
      }

      if (this.isReviewer || this.isPrimaryReviewer) {
         return true
      }

      // Amy Miller, Todd Ingersoll, Vicky Brown, and Jen Weitzel
      if (
         this.loggedInUserId == 696 ||
         this.loggedInUserId == 10399 ||
         this.loggedInUserId == 10449 ||
         (this.loggedInUserId == 10423 && this.reviewType != 'DO')
      ) {
         return true
      }

      if (this.loggedInUserId == 10423 && this.reviewType == 'DO' && this.userReview?.isCompleted) {
         return true
      }

      return false
   }

   canShowFinalFeedbackSection(): boolean {
      return this.isDVMSubmitted && this.isCOSSubmitted && this.isReviewer
   }

   isFollowUpIncomplete(): boolean {
      if (!this.followUpRequired) return false
      if (!this.followUpNotes?.trim()) return true
      if (!this.followUpBy) return true
      if (this.followUpBy === 'Other' && !this.followUpByOther?.trim()) return true
      if (!this.followUpTimeframe) return true
      if (this.followUpTimeframe === 'Open' && !this.followUpTimeframeOpen?.trim()) return true
      return false
   }

   isFormSubmitted(): boolean {
      // Reviewee can't edit after they submit
      if (!this.isReviewer && this.isDVMSubmitted) {
         return true
      }

      return false
   }

   isFormReadOnly(): boolean {
      if (this.reviewType == 'DVM' && this.isHM) {
         return true
      }

      if (this.isUserDeferred()) {
         return true
      }

      // Nobody can edit after completion
      if (this.userReview?.isCompleted) {
         return true
      }
      return false
   }

   canRevieweeEdit(): boolean {
      // Only the actual reviewee can edit the reviewee section
      if (this.isPrimaryReviewee && !this.userReview?.isCompleted) {
         return true
      }

      // Reviewees can edit only if they haven't submitted and form isn't completed
      //return !this.isDVMSubmitted && !this.userReview?.isCompleted

      // Reviewees can edit only if the form isn't completed
      return false
   }

   canReviewerEdit(): boolean {
      // Only the actual reviewer can edit the reviewer section
      if (!this.isPrimaryReviewer) {
         return false
      }

      // Note: Reviewer can continue editing even after they submit, until they complete
      return this.isReviewer && !this.userReview?.isCompleted
   }

   shouldShowRevieweeQuestions(): boolean {
      if (this.printerModalOpened) {
         return this.printReviewee ? true : false
      }

      if (this.isPrimaryReviewee || this.isPrimaryReviewer || this.isReviewer) {
         return true
      }

      // 3. Form is completed (everyone can view)
      if (this.userReview?.isCompleted) {
         return true
      }

      // Amy Miller, Todd Ingersoll, Vicky Brown, and Jen Weitzel
      if (this.loggedInUserId == 696 || this.loggedInUserId == 10399 || this.loggedInUserId == 10449 || this.loggedInUserId == 10423) {
         return true
      }

      return false
   }

   shouldShowReviewerQuestions(): boolean {
      if (this.printerModalOpened) {
         return this.printReviewer ? true : false
      }

      // 1. Form is submitted (everyone can see everything)
      if (this.isPrimaryReviewer || this.isReviewer) {
         return true
      }

      if (this.userReview?.isCompleted) {
         return true
      }

      // Amy Miller, Todd Ingersoll, Vicky Brown, and Jen Weitzel
      if (
         this.loggedInUserId == 696 ||
         this.loggedInUserId == 10399 ||
         this.loggedInUserId == 10449 ||
         (this.loggedInUserId == 10423 && this.reviewType != 'DO')
      ) {
         return true
      }

      if (this.loggedInUserId == 10423 && this.reviewType == 'DO' && this.userReview?.isCompleted) {
         return true
      }

      return false
   }

   getNewReviewerValue(): string {
      return this['new' + this.getReviewerLabel(true)]
   }

   getRevieweeSubmittedDatetime(): string {
      if (!this.userReview?.dvmSubmittedOn) {
         return ''
      }
      return dayjs.utc(this.userReview.dvmSubmittedOn).local().format('MM/DD/YYYY hh:mm A')
   }

   getReviewerSubmittedDatetime(): string {
      if (!this.userReview?.cosSubmittedOn) {
         return ''
      }
      return dayjs.utc(this.userReview.cosSubmittedOn).local().format('MM/DD/YYYY hh:mm A')
   }

   getSubmitButtonText(): string {
      if (this.canShowFinalFeedbackSection()) {
         return 'Complete & Finalize Review'
      } else if (this.isReviewer && this.isCOSSubmitted) {
         return 'Save Changes'
      }
      return this.isReviewer ? `Submit ${this.getReviewerLabel(true)} Review` : `Submit ${this.getRevieweeLabel(true)} Review`
   }

   private scrollToFirstMissing(first: string) {
      if (first.startsWith('profile:')) {
         const id = first.replace('profile:', '')
         // map keys to actual element ids
         const elId =
            id === 'communicationPreference'
               ? 'communicationPreference'
               : id === 'email'
                 ? 'emailInput'
                 : id === 'phone'
                   ? 'phoneInput'
                   : id === 'vetSchool'
                     ? 'vetSchoolDropdown'
                     : id === 'gradYear'
                       ? 'gradYearInput'
                       : id === 'yearsExperience'
                         ? 'yearsExperienceRadio'
                         : id === 'lendAHand'
                           ? 'lendAHandRadio'
                           : id === 'boardCertified'
                             ? 'boardCertifiedRadio'
                             : id === 'boardCertificationName'
                               ? 'boardCertificationInput'
                               : id === 'leaderAspirations'
                                 ? 'leaderAspirationsRadio'
                                 : id === 'medicalInterests'
                                   ? 'medical-interests-card'
                                   : null

         if (elId) document.getElementById(elId)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
         return
      }

      const [who, sectionId, qId] = first.split(':')
      document.getElementById(`${sectionId}_${qId}_${who}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
   }

   populateDropDowns(): void {
      this.userReviewClient.getDvmToolSelection(DVMToolSelectionType.Education).subscribe({
         next: (val: any) => {
            this.educationOptions = val.map((item) => {
               return { id: item.id, name: item.description }
            })
         },
      })

      this.userReviewClient.getDvmToolSelection(DVMToolSelectionType.MedicalInterests).subscribe({
         next: (val: any) => {
            this.medicalInterestOptions = val.map((item) => {
               return { id: item.id, name: item.description, medExpertise: false }
            })
         },
      })
   }

   onCommunicationCheckboxChange(value: string, checked: boolean) {
      if (checked) {
         if (!this.selectedCommunicationPreferences.includes(value)) {
            this.selectedCommunicationPreferences.push(value)
         }
      } else {
         this.selectedCommunicationPreferences = this.selectedCommunicationPreferences.filter((v) => v !== value)
      }
   }

   onCheckboxChange(array: string[], value: string, checked: boolean, maxLimit?: number) {
      const index = array.indexOf(value)

      if (checked) {
         // Check if we've reached the max limit
         if (maxLimit && array.length >= maxLimit) {
            // Don't add the item, we've reached the limit
            return
         }
         if (index === -1) {
            array.push(value)
         }
      } else {
         if (index > -1) {
            array.splice(index, 1)
         }
      }
   }

   isLimitReached(array: string[], otherChecked: boolean, maxLimit: number, excludeValue?: string): boolean {
      const count = array.length + (otherChecked ? 1 : 0)
      if (excludeValue && array.includes(excludeValue)) {
         return count - 1 >= maxLimit
      }
      return count >= maxLimit
   }

   handleMedicalInterestsChanged(selection: IDropdownOption[]) {
      this.medicalInterests = selection
   }

   handleMedicalExpertiseChanged(selection: IDropdownOption[]) {
      this.medicalExpertise = selection
   }

   handleUniversityChanged(event: IDropdownOption) {
      this.selectedUniversity = event
   }

   handleVersionChanged(selection: IDropdownOption) {
      this.lastUpdatedDate = selection.name
      this.loadUserReview()
   }

   getLastSavedDatetime(): string {
      const localDate = dayjs.utc(this.userReview.dateUpdated).local()
      return localDate.format('MM/DD/YYYY') + ' ' + localDate.format('h:mm A')
   }

   getSubmittedDatetime(): string {
      const localDate = dayjs.utc(this.userReview?.submittedOn).local()
      return localDate.format('MM/DD/YYYY') + ' ' + localDate.format('h:mm A')
   }

   getCompletedDatetime(): string {
      const localDate = dayjs.utc(this.userReview?.completedOn).local()
      return localDate.format('MM/DD/YYYY') + ' ' + localDate.format('h:mm A')
   }

   async printReview() {
      if (this.isTranslating) return

      if (!this.printInSpanish) {
         setTimeout(() => {
            window.print()
            this.closePrintChoiceModal()
         }, 400)
         return
      }

      // Let the chosen print sections render first
      this.cdr.detectChanges()
      await new Promise((r) => setTimeout(r, 400))

      const nodes = this.collectPrintTextNodes()
      const texts = Array.from(new Set(nodes.map((n) => n.nodeValue!.trim())))

      let lookup = new Map<string, string>()
      this.isTranslating = true
      try {
         const baseUrl = (this.userReviewClient as any).baseUrl ?? ''
         const pairs = await firstValueFrom(
            this.http.post<{ source: string; translated: string }[]>(`${baseUrl}/api/userreview/translate-es`, texts)
         )
         lookup = new Map((pairs ?? []).map((p) => [p.source, p.translated]))
      } catch {
         this.toastr.error('Unable to translate right now. Please try again or print in English.')
         return
      } finally {
         this.isTranslating = false
      }

      // Swap in Spanish, print, then restore the English
      const originals = nodes.map((n) => n.nodeValue)
      nodes.forEach((n) => {
         const raw = n.nodeValue!
         const es = lookup.get(raw.trim())
         if (es) n.nodeValue = raw.replace(raw.trim(), es)
      })

      setTimeout(() => {
         window.print()
         nodes.forEach((n, i) => (n.nodeValue = originals[i]))
         this.closePrintChoiceModal()
      }, 300)
   }

   /** Text that will appear on the printout (skips screen-only parts, buttons, the dialog). */
   private collectPrintTextNodes(): Text[] {
      const root = document.querySelector('dvm-tool-plan') ?? document.body
      const skip =
         '.screens-only, .print-modal, .versionsCard, .admin-only, .cycle-closed-notice, .final-feedback-section, .autosave-toast, peer-input-panel, button, script, style'

      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
         acceptNode: (node) => {
            const text = node.nodeValue?.trim() ?? ''
            if (text.length < 2 || /^[\d\s.,:;/%$#()+\-—–]+$/.test(text)) return NodeFilter.FILTER_REJECT
            if (!node.parentElement || node.parentElement.closest(skip)) return NodeFilter.FILTER_REJECT
            return NodeFilter.FILTER_ACCEPT
         },
      })

      const nodes: Text[] = []
      while (walker.nextNode()) nodes.push(walker.currentNode as Text)
      return nodes
   }

   openPrintChoiceModal(): void {
      this.printerModalOpened = true
      this.printReviewee = false
      this.printReviewer = false
      this.printInSpanish = false
      this.printChoiceDialog.set({ show: true })
   }

   // --- Peer Input ---

   /** The Peer Input card shows while the manager can still edit their side of the review. */
   canRequestPeerInput(): boolean {
      return !!this.userReview && this.reviewType !== 'TEAM' && this.canReviewerEdit() && !this.isFormReadOnly() && !this.printerModalOpened
   }

   get peerRevieweeUserId(): number {
      return Number(this.userReview?.revieweeId || this.revieweeId)
   }

   /** Writes the chosen peer answers into the manager's side; autosave picks them up like typed answers. */
   applyPeerAnswers(responses: PeerInputResponse[]): void {
      const sections = this.reviewerSections
      if (!sections || !this.canReviewerEdit() || !responses?.length) {
         this.toastr.error('Your review cannot be edited right now.')
         return
      }

      for (const r of responses) {
         sections.setValue(r.questionId, 'reviewer', parseResponseValue(r.responseValue))
         sections.setNotes(r.questionId, 'reviewer', r.notes ?? '')
         sections.setOtherText(r.questionId, 'reviewer', r.otherText ?? '')
      }

      this.toastr.success(`${responses.length} ${responses.length === 1 ? 'answer' : 'answers'} copied into your review.`)
   }

   closePrintChoiceModal(): void {
      this.printerModalOpened = false
      this.printChoiceDialog.set({ show: false })

      this.printReviewee = false
      this.printReviewer = false
   }

   loadYoY(practiceSKey: number): void {
      this.isLoadingYoY = true

      this.userReviewClient.getYoYFinancials(practiceSKey).subscribe({
         next: (results: any[]) => {
            this.YoYResults = results ?? []
            this.isLoadingYoY = false
         },
         error: (err) => {
            console.error(err)
            this.YoYResults = []
            this.isLoadingYoY = false
         },
      })
   }

   loadReviewAttachments(): void {
      if (!this.revieweeId || this.revieweeId === 'new') return

      this.userReviewClient.getReviewAttachments(Number(this.revieweeId), this.reviewType).subscribe({
         next: (items) => {
            this.reviewAttachments = items ?? []
            if (this.reviewAttachments.length > 0) {
               this.adminOnlyAttachmentExpanded = true
            }
         },
         error: () => {},
      })
   }

   onAttachmentSelected(event: Event): void {
      const input = event.target as HTMLInputElement
      const file = input.files?.[0]
      input.value = '' // lets the same file be re-selected later
      if (!file) return

      const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
      if (!isPdf) {
         this.toastr.error('Only PDF files can be attached.')
         return
      }
      if (file.size > 10 * 1024 * 1024) {
         this.toastr.error('PDF must be under 10 MB.')
         return
      }

      this.isUploadingAttachment = true

      const form = new FormData()
      form.append('file', file, file.name)
      form.append('revieweeUserId', this.revieweeId)
      form.append('reviewType', this.reviewType)

      this.isUploadingAttachment = true
      this.http.post((this.userReviewClient as any).baseUrl + '/api/userreview/admin/review-attachments', form).subscribe({
         next: () => {
            this.isUploadingAttachment = false
            this.toastr.success('Attachment uploaded')
            this.loadReviewAttachments()
         },
         error: () => {
            this.isUploadingAttachment = false
            this.toastr.error('Failed to upload attachment')
         },
      })

      /*
      const fileParam: FileParameter = { data: file, fileName: file.name }
      
      ;(this.userReviewClient as any).uploadReviewAttachment(fileParam, Number(this.revieweeId), this.reviewType).subscribe({
         next: () => {
            this.isUploadingAttachment = false
            this.toastr.success('Attachment uploaded')
            this.loadReviewAttachments()
         },
         error: () => {
            this.isUploadingAttachment = false
            this.toastr.error('Failed to upload attachment')
         },
      })*/
   }

   openAttachment(a: ReviewAttachmentDto): void {
      ;(this.userReviewClient as any).downloadReviewAttachment(a.id).subscribe({
         next: (response: any) => {
            const blob: Blob = response?.data ?? response
            const url = URL.createObjectURL(blob)
            window.open(url, '_blank')
            setTimeout(() => URL.revokeObjectURL(url), 60_000)
         },
         error: () => this.toastr.error('Failed to open attachment'),
      })
   }

   downloadAttachment(a: ReviewAttachmentDto, event: Event): void {
      event.stopPropagation()
      const baseUrl = (this.userReviewClient as any).baseUrl ?? ''

      this.http.get(`${baseUrl}/api/userreview/review-attachments/${a.id}/download`, { responseType: 'blob' }).subscribe({
         next: (blob: Blob) => {
            const url = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = a.fileName ?? 'review.pdf'
            link.click()
            URL.revokeObjectURL(url)
         },
         error: () => this.toastr.error('Failed to download attachment'),
      })
   }

   deleteAttachment(a: ReviewAttachmentDto, event: Event): void {
      event.stopPropagation()
      if (!confirm('Remove this attachment?')) return
      ;(this.userReviewClient as any).deleteReviewAttachment(a.id).subscribe({
         next: () => {
            this.reviewAttachments = this.reviewAttachments.filter((x) => x.id !== a.id)
            this.toastr.success('Attachment removed')
         },
         error: () => this.toastr.error('Failed to remove attachment'),
      })
   }

   formatUploadedOn(date: string): string {
      return dayjs.utc(date).local().format('MM/DD/YYYY [at] h:mm A')
   }

   resetReview(id: any): void {
      const confirmed = window.confirm('Are you sure you want to reset this review? This cannot be undone.')

      if (!confirmed) {
         return
      }

      this.userReviewClient.resetReview(id).subscribe({
         next: () => {
            this.toastr.success('This review has been reset')
            window.location.reload()
         },
         error: () => {},
      })
   }

   resetReviewee(id: any): void {
      const confirmed = window.confirm('Are you sure you want to erase the reviewees answers? This cannot be undone.')

      if (!confirmed) {
         return
      }

      this.userReviewClient.resetReviewReviewee(id).subscribe({
         next: () => {
            this.toastr.success('This self-assessment has been reset')
            window.location.reload()
         },
         error: () => {},
      })
   }

   resetReviewer(id: any): void {
      const confirmed = window.confirm('Are you sure you want to erase the reviewers answers? This cannot be undone.')

      if (!confirmed) {
         return
      }

      this.userReviewClient.resetReviewReviewer(id).subscribe({
         next: () => {
            this.toastr.success('This performance review has been reset')
            window.location.reload()
         },
         error: () => {},
      })
   }
}
